import React, { useState, useEffect } from 'react';
import { getReunionTypeShort } from '../utils/reunionTypes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

export default function ModifierStagiaires() {
  const navigate = useNavigate();
  const { organismeActif } = useOrganisme();
  
  const [membresActuels, setMembresActuels] = useState([]);
  const [eligibles, setEligibles] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Modifications en cours
  const [retraits, setRetraits] = useState([]);
  const [ajouts, setAjouts] = useState([]);
  
  // Modal ajout
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [modalForm, setModalForm] = useState({
    reunionNominationId: '',
  });

  // Modal retrait
  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [membreARetirer, setMembreARetirer] = useState(null);
  const [retraitForm, setRetraitForm] = useState({
    raisonRetrait: '',
    raisonRetraitAutre: '',
    dateRetrait: '',
  });

  // Modal confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [generatePdf, setGeneratePdf] = useState(false);

  useEffect(() => {
    if (organismeActif === 'ADAR') {
      fetchData();
    } else {
      navigate('/admin/composition-conseil');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organismeActif]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [compositionRes, eligiblesRes, reunionsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/eligibles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/reunions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      const membresStagiaires = compositionRes.data.actifs.filter(m => m.type === 'stagiaire');
      setMembresActuels(membresStagiaires);
      setEligibles(eligiblesRes.data);
      setReunions(reunionsRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setSelectedAdherent(null);
    setSearchTerm('');
    setModalForm({
      reunionNominationId: '',
    });
    setShowModal(true);
  };

  const handleAddMembre = () => {
    if (!selectedAdherent) {
      alert('Veuillez sélectionner un adhérent');
      return;
    }

    if (!modalForm.reunionNominationId) {
      alert('Veuillez sélectionner une réunion de nomination');
      return;
    }

    setAjouts([...ajouts, {
      adherentId: selectedAdherent._id,
      adherentNom: `${selectedAdherent.prenom} ${selectedAdherent.nom}`,
      type: 'stagiaire',
      reunionElectionId: modalForm.reunionNominationId,
      reunionElection: reunions.find(r => r._id === modalForm.reunionNominationId),
    }]);
    setShowModal(false);
  };

  const handleCancelAdd = (adherentId) => {
    setAjouts(ajouts.filter(a => a.adherentId !== adherentId));
  };

  const handleOpenRetraitModal = (membre) => {
    setMembreARetirer(membre);
    setRetraitForm({
      raisonRetrait: '',
      raisonRetraitAutre: '',
      dateRetrait: '',
    });
    setShowRetraitModal(true);
  };

  const handleConfirmRetrait = () => {
    if (!retraitForm.raisonRetrait) {
      alert('Veuillez sélectionner une raison de retrait');
      return;
    }

    if (retraitForm.raisonRetrait === 'autre' && !retraitForm.raisonRetraitAutre) {
      alert('Veuillez préciser la raison du retrait');
      return;
    }

    setRetraits([...retraits, {
      membreId: membreARetirer._id,
      raisonRetrait: retraitForm.raisonRetrait,
      raisonRetraitAutre: retraitForm.raisonRetraitAutre,
      dateRetrait: retraitForm.dateRetrait || new Date().toISOString().split('T')[0],
    }]);
    setShowRetraitModal(false);
    setMembreARetirer(null);
  };

  const handleCancelRetrait = (membreId) => {
    setRetraits(retraits.filter(r => r.membreId !== membreId));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      await axios.put(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/masse`,
        { ajouts, retraits, modifications: [], generatePdf },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate('/admin/composition-conseil');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setShowConfirmModal(false);
    }
  };

  const isMarkedForRetrait = (membreId) => retraits.some(r => r.membreId === membreId);

  const adherentsFiltered = eligibles.filter(adherent => {
    const matchSearch = !searchTerm || 
      `${adherent.prenom} ${adherent.nom}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    const notAlreadyAdded = !ajouts.some(a => a.adherentId === adherent._id);
    const notAlreadyMembre = !membresActuels.some(m => m.adherent?._id === adherent._id && !isMarkedForRetrait(m._id));
    
    return matchSearch && notAlreadyAdded && notAlreadyMembre;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/composition-conseil')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gérer les Administrateurs Stagiaires
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                ADAR - Nombre illimité de stagiaires
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/composition-conseil')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={ajouts.length === 0 && retraits.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enregistrer les modifications
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Administrateurs Stagiaires ({membresActuels.length + ajouts.length - retraits.length})
            </h2>
            <button
              onClick={handleOpenAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <UserPlusIcon className="h-5 w-5" />
              Ajouter un stagiaire
            </button>
          </div>

          {membresActuels.length === 0 && ajouts.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              Aucun administrateur stagiaire pour le moment
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date de nomination</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Membres actuels */}
                  {membresActuels.map((membre) => {
                    const markedForRetrait = isMarkedForRetrait(membre._id);
                    return (
                      <tr key={membre._id} className={markedForRetrait ? 'bg-red-50' : 'hover:bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={markedForRetrait ? 'text-red-700 line-through' : 'text-gray-900'}>
                            {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {membre.reunionElection?.date 
                            ? new Date(membre.reunionElection.date).toLocaleDateString('fr-FR')
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {markedForRetrait ? (
                            <button
                              onClick={() => handleCancelRetrait(membre._id)}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              Annuler le retrait
                            </button>
                          ) : (
                            <button
                              onClick={() => handleOpenRetraitModal(membre)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Retirer
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Ajouts en cours */}
                  {ajouts.map((ajout) => (
                    <tr key={ajout.adherentId} className="bg-green-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span className="text-green-700">
                          {ajout.adherentNom} <span className="text-xs">(à ajouter)</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {ajout.reunionElection?.date 
                          ? new Date(ajout.reunionElection.date).toLocaleDateString('fr-FR')
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleCancelAdd(ajout.adherentId)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Annuler
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Ajout */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Ajouter un administrateur stagiaire
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Réunion de nomination *
                </label>
                <select
                  value={modalForm.reunionNominationId}
                  onChange={(e) => setModalForm({ ...modalForm, reunionNominationId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Sélectionner une réunion</option>
                  {reunions.map(reunion => (
                    <option key={reunion._id} value={reunion._id}>
                      {getReunionTypeShort(reunion.type, 'ADAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rechercher un adhérent *
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Nom ou prénom..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {searchTerm && (
                  <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    {adherentsFiltered.slice(0, 10).map((adherent) => (
                      <button
                        key={adherent._id}
                        onClick={() => {
                          setSelectedAdherent(adherent);
                          setSearchTerm('');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        {adherent.prenom} {adherent.nom}
                      </button>
                    ))}
                    {adherentsFiltered.length === 0 && (
                      <p className="px-3 py-2 text-gray-500 text-sm">Aucun adhérent trouvé</p>
                    )}
                  </div>
                )}

                {selectedAdherent && (
                  <div className="mt-2 flex items-center justify-between p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <span className="font-medium text-indigo-800">
                      {selectedAdherent.prenom} {selectedAdherent.nom}
                    </span>
                    <button
                      onClick={() => setSelectedAdherent(null)}
                      className="text-indigo-600 hover:text-indigo-800"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleAddMembre}
                disabled={!selectedAdherent}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Retrait */}
      {showRetraitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Retirer un stagiaire
              </h3>
              <button onClick={() => setShowRetraitModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Stagiaire :</span> {membreARetirer?.adherent?.prenom} {membreARetirer?.adherent?.nom}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison du retrait *
                </label>
                <select
                  value={retraitForm.raisonRetrait}
                  onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetrait: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Sélectionner une raison</option>
                  <option value="demission">Démission</option>
                  <option value="non_adhesion">Non-adhésion</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              {retraitForm.raisonRetrait === 'autre' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Préciser la raison *
                  </label>
                  <input
                    type="text"
                    value={retraitForm.raisonRetraitAutre}
                    onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetraitAutre: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de retrait
                </label>
                <input
                  type="date"
                  value={retraitForm.dateRetrait}
                  onChange={(e) => setRetraitForm({ ...retraitForm, dateRetrait: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowRetraitModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmRetrait}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirmer le retrait
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Composition finale
            </h3>
            
            <p className="text-gray-600 mb-4">
              Vous êtes sur le point d'enregistrer les modifications de la composition du conseil.
            </p>

            <div className="mb-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={generatePdf}
                  onChange={(e) => setGeneratePdf(e.target.checked)}
                  className="h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enregistrer la composition en PDF
                </span>
              </label>
              <p className="ml-8 mt-1 text-xs text-gray-500">
                Le PDF sera disponible dans la section "Documents de composition"
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setGeneratePdf(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Enregistrement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
